import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { OrgHierarchyWeb } from './enrollmentDataOrgWeb';
import { isPermanentPeriod } from '../constants/groupPeriods';
import './EnrollmentDataManager.css';

/**
 * Enrollment Data Manager — compact DB-table browser for groups → periods → GPs → UGPs → app data.
 * Polymorphic relationships are a filterable table, not the primary navigation surface.
 * GP / group / period deletes ask about cascade (child GPs or GPs using that group/period).
 */

const TABLE_KINDS = [
  { id: 'groups_periods', label: 'groups_periods', short: 'GP' },
  { id: 'user_groups_periods', label: 'user_groups_periods', short: 'UGP' },
  { id: 'relationships', label: 'relationships', short: 'Rel' },
  { id: 'groups', label: 'groups', short: 'Group' },
  { id: 'periods', label: 'periods', short: 'Period' },
  { id: 'student_info', label: 'student_info', short: 'SI' },
  { id: 'credentials_keys', label: 'credentials_keys', short: 'Cred' },
  { id: 'documents', label: 'documents', short: 'Doc' },
  { id: 'form_submissions', label: 'form_submissions', short: 'Form' }
];

const ENTITY_DELETE_TABLES = new Set([
  'student_info',
  'credentials_keys',
  'documents',
  'form_submissions',
  'relationships'
]);

const EnrollmentDataManager = () => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [kind, setKind] = useState('groups_periods');
  const [search, setSearch] = useState('');
  const [relType, setRelType] = useState('all');
  const [relKind, setRelKind] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  const [orphansOnly, setOrphansOnly] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'web'
  const [webShowIds, setWebShowIds] = useState(false);
  const [cascadeTarget, setCascadeTarget] = useState(null);
  const [cascadePreview, setCascadePreview] = useState(null);
  const [cascadeLoading, setCascadeLoading] = useState(false);
  const [cascadeOpts, setCascadeOpts] = useState({
    cascadeChildGps: true,
    deleteOrphanGroup: false,
    deleteOrphanPeriod: false
  });
  const [spineTarget, setSpineTarget] = useState(null);
  const [spineCascadeGps, setSpineCascadeGps] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const authHeaders = useMemo(() => ({ headers: { 'Content-Type': 'application/json' } }), []);

  const loadSnapshot = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch('/api/data-cleanup-manager/snapshot', authHeaders);
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data.error || data.message || 'Request failed');
      }
      setSnapshot(data);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load enrollment data.');
      setSnapshot(null);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadSnapshot();
  }, [loadSnapshot]);

  const counts = snapshot?.counts || {};
  const organizations = snapshot?.organizations || [];

  const gpsByGroupId = useMemo(() => {
    const map = {};
    (snapshot?.groups_periods || []).forEach((gp) => {
      if (!gp.group_id) return;
      const key = String(gp.group_id);
      if (!map[key]) map[key] = [];
      map[key].push(gp);
    });
    return map;
  }, [snapshot]);

  const gpsByPeriodId = useMemo(() => {
    const map = {};
    (snapshot?.groups_periods || []).forEach((gp) => {
      if (!gp.period_id) return;
      const key = String(gp.period_id);
      if (!map[key]) map[key] = [];
      map[key].push(gp);
    });
    return map;
  }, [snapshot]);

  const relTypeOptions = useMemo(() => {
    const types = new Set();
    (snapshot?.relationships || []).forEach((r) => {
      if (r.relationship_type) types.add(r.relationship_type);
    });
    return ['all', ...Array.from(types).sort()];
  }, [snapshot]);

  const rows = useMemo(() => {
    if (!snapshot) return [];
    const q = search.trim().toLowerCase();
    const match = (...parts) => {
      if (!q) return true;
      return parts.filter(Boolean).join(' ').toLowerCase().includes(q);
    };
    const orphanGate = (row) => !orphansOnly || row.is_orphan;
    const missingExtra = (row, fallback) =>
      row.is_orphan ? `missing ${(row.missing_sides || []).join(', ') || 'target'}` : fallback;

    if (kind === 'groups') {
      return (snapshot.groups || [])
        .filter((g) => {
          const gps = gpsByGroupId[String(g.id)] || [];
          const orphan = !!g.is_orphan || gps.length === 0;
          return orphanGate({ is_orphan: orphan }) && match(g.name, g.group_type, g.id, g.description);
        })
        .map((g) => {
          const gps = gpsByGroupId[String(g.id)] || [];
          const enrollments = gps.reduce((n, gp) => n + (gp.enrollments ?? 0), 0);
          const orphan = !!g.is_orphan || gps.length === 0;
          return {
            key: `g-${g.id}`,
            id: g.id,
            table: 'groups',
            label: g.name,
            meta: g.group_type || '—',
            extra: missingExtra(
              { is_orphan: orphan, missing_sides: g.missing_sides || ['groups_periods'] },
              [`${gps.length} gp`, `${enrollments} ugp`, g.description].filter(Boolean).join(' · ')
            ),
            gpCount: gps.length,
            enrollments,
            orphan,
            raw: g
          };
        });
    }

    if (kind === 'periods') {
      return (snapshot.periods || [])
        .filter((p) => {
          const gps = gpsByPeriodId[String(p.id)] || [];
          const orphan = !!p.is_orphan || gps.length === 0;
          return orphanGate({ is_orphan: orphan }) && match(p.title, p.code, p.id);
        })
        .map((p) => {
          const gps = gpsByPeriodId[String(p.id)] || [];
          const enrollments = gps.reduce((n, gp) => n + (gp.enrollments ?? 0), 0);
          const permanent = isPermanentPeriod(p);
          const orphan = !!p.is_orphan || gps.length === 0;
          return {
            key: `p-${p.id}`,
            id: p.id,
            table: 'periods',
            label: p.title || p.code || p.id,
            meta: permanent
              ? 'permanent'
              : [p.start_date, p.end_date].filter(Boolean).join(' → ') || '—',
            extra: missingExtra(
              { is_orphan: orphan, missing_sides: p.missing_sides || ['groups_periods'] },
              [`${gps.length} gp`, `${enrollments} ugp`, p.code].filter(Boolean).join(' · ')
            ),
            gpCount: gps.length,
            enrollments,
            permanent,
            orphan,
            raw: p
          };
        });
    }

    if (kind === 'groups_periods') {
      return (snapshot.groups_periods || [])
        .filter((gp) => {
          if (!orphanGate(gp)) return false;
          if (orgFilter !== 'all' && !gp.is_orphan) {
            const tree = snapshot.orgTrees?.[orgFilter];
            if (!tree) return false;
            const ids = new Set([String(tree.org?.id), ...(tree.teams || []).map((t) => String(t.gp?.id))]);
            if (!ids.has(String(gp.id))) return false;
          }
          return match(
            gp.label,
            gp.group_name,
            gp.period_title,
            gp.period_code,
            gp.id,
            gp.group_type,
            gp.is_orphan ? 'orphan missing' : ''
          );
        })
        .map((gp) => ({
          key: `gp-${gp.id}`,
          id: gp.id,
          table: 'groups_periods',
          label: gp.label || gp.group_name || gp.id,
          meta: [
            gp.is_organization ? 'org' : gp.group_type,
            gp.period_title || gp.period_code || (gp.period_id ? 'period ✕' : 'no period')
          ]
            .filter(Boolean)
            .join(' · '),
          extra: missingExtra(
            gp,
            `${gp.enrollments ?? 0} ugp · ${gp.attachments ?? 0} attach · ${gp.hierarchy_children ?? 0} child`
          ),
          orphan: !!gp.is_orphan,
          counts: gp,
          raw: gp
        }));
    }

    if (kind === 'user_groups_periods') {
      return (snapshot.user_groups_periods || [])
        .filter((u) => {
          if (!orphanGate(u)) return false;
          if (orgFilter !== 'all' && !u.is_orphan) {
            const tree = snapshot.orgTrees?.[orgFilter];
            if (!tree) return false;
            const gpIds = new Set([String(tree.org?.id), ...(tree.teams || []).map((t) => String(t.gp?.id))]);
            if (!gpIds.has(String(u.groups_periods_id))) return false;
          }
          return match(
            u.user_name,
            u.email,
            u.id,
            u.gp_label,
            u.groups_periods_id,
            u.is_orphan ? 'orphan missing' : ''
          );
        })
        .map((u) => ({
          key: `ugp-${u.id}`,
          id: u.id,
          table: 'user_groups_periods',
          label: u.user_name || u.email || u.id,
          meta: u.gp_label || (u.gp_exists === false ? `gp ${u.groups_periods_id} ✕` : u.groups_periods_id),
          extra: missingExtra(
            u,
            [
              u.email,
              u.is_current_org_pointer ? 'current_ugp' : null,
              `${(u.student_info || 0) + (u.credentials_keys || 0) + (u.documents || 0) + (u.person_links || 0) + (u.form_submissions || 0)} attach`
            ]
              .filter(Boolean)
              .join(' · ')
          ),
          orphan: !!u.is_orphan,
          raw: u
        }));
    }

    if (kind === 'relationships') {
      return (snapshot.relationships || [])
        .filter((r) => {
          if (!orphanGate(r)) return false;
          if (relType !== 'all' && r.relationship_type !== relType) return false;
          if (relKind === 'orphan') {
            if (!r.is_orphan) return false;
          } else if (relKind !== 'all' && r.kind !== relKind) {
            return false;
          }
          if (orgFilter !== 'all') {
            if (r.is_orphan && (relKind === 'orphan' || orphansOnly)) {
              // keep
            } else {
              const tree = snapshot.orgTrees?.[orgFilter];
              if (!tree) return false;
              const gpIds = new Set([
                String(tree.org?.id),
                ...(tree.teams || []).map((t) => String(t.gp?.id))
              ]);
              const ugpIds = new Set();
              (tree.teams || []).forEach((t) => {
                (t.enrollments || []).forEach((e) => ugpIds.add(String(e.ugp?.id)));
              });
              (tree.org_enrollments || []).forEach((e) => ugpIds.add(String(e.ugp?.id)));
              const touches =
                (r.parent_table === 'groups_periods' && gpIds.has(String(r.parent_id))) ||
                (r.child_table === 'groups_periods' && gpIds.has(String(r.child_id))) ||
                (r.parent_table === 'user_groups_periods' && ugpIds.has(String(r.parent_id))) ||
                (r.child_table === 'user_groups_periods' && ugpIds.has(String(r.child_id)));
              if (!touches) return false;
            }
          }
          return match(
            r.relationship_type,
            r.parent_table,
            r.child_table,
            r.parent_label,
            r.child_label,
            r.other_label,
            r.parent_id,
            r.child_id,
            r.id,
            r.is_orphan ? 'orphan missing' : ''
          );
        })
        .map((r) => {
          const parentBit = r.parent_exists
            ? `${r.parent_table}.${r.parent_id}${r.parent_label ? ` (${r.parent_label})` : ''}`
            : `${r.parent_table}.${r.parent_id} ✕`;
          const childBit = r.child_exists
            ? `${r.child_table}.${r.child_id}${r.child_label ? ` (${r.child_label})` : ''}`
            : `${r.child_table}.${r.child_id} ✕`;
          return {
            key: `rel-${r.id}`,
            id: r.id,
            table: 'relationships',
            label: r.relationship_type || '—',
            meta: `${parentBit} → ${childBit}`,
            extra: missingExtra(r, [r.kind, r.other_label].filter(Boolean).join(' · ')),
            orphan: !!r.is_orphan,
            raw: r
          };
        });
    }

    if (kind === 'student_info') {
      return (snapshot.student_info || [])
        .filter(
          (r) =>
            orphanGate(r) &&
            match(r.status, r.grade_level, r.id, r.user_groups_periods_id, r.is_orphan ? 'orphan' : '')
        )
        .map((r) => ({
          key: `si-${r.id}`,
          id: r.id,
          table: 'student_info',
          label: r.status || 'student_info',
          meta: r.grade_level || '—',
          extra: missingExtra(
            r,
            `ugp ${r.user_groups_periods_id}${r.ugp_exists === false ? ' ✕' : ''}`
          ),
          orphan: !!r.is_orphan,
          raw: r
        }));
    }

    if (kind === 'credentials_keys') {
      return (snapshot.credentials_keys || [])
        .filter(
          (r) =>
            orphanGate(r) &&
            match(r.platform_label, r.title, r.entry_type, r.source_slug, r.id, r.is_orphan ? 'orphan' : '')
        )
        .map((r) => ({
          key: `ck-${r.id}`,
          id: r.id,
          table: 'credentials_keys',
          label: r.platform_label || r.title || 'credential',
          meta: [r.entry_type, r.source_slug].filter(Boolean).join(' · ') || '—',
          extra: missingExtra(
            r,
            `ugp ${r.user_groups_periods_id}${r.ugp_exists === false ? ' ✕' : ''}`
          ),
          orphan: !!r.is_orphan,
          raw: r
        }));
    }

    if (kind === 'documents') {
      return (snapshot.documents || [])
        .filter(
          (r) =>
            orphanGate(r) &&
            match(r.title, r.category, r.file_name, r.source_slug, r.id, r.is_orphan ? 'orphan' : '')
        )
        .map((r) => ({
          key: `doc-${r.id}`,
          id: r.id,
          table: 'documents',
          label: r.title || r.file_name || 'document',
          meta: [r.category, r.source_slug].filter(Boolean).join(' · ') || '—',
          extra: missingExtra(
            r,
            `ugp ${r.user_groups_periods_id}${r.ugp_exists === false ? ' ✕' : ''}`
          ),
          orphan: !!r.is_orphan,
          raw: r
        }));
    }

    if (kind === 'form_submissions') {
      return (snapshot.form_submissions || [])
        .filter(
          (r) =>
            orphanGate(r) &&
            match(r.form_title, r.id, r.ugp_id, r.form_id, r.is_orphan ? 'orphan' : '')
        )
        .map((r) => ({
          key: `fs-${r.id}`,
          id: r.id,
          table: 'form_submissions',
          label: r.form_title || 'submission',
          meta: r.form_exists === false ? `form ${r.form_id} ✕` : 'form_submissions',
          extra: missingExtra(r, `ugp ${r.ugp_id}${r.ugp_exists === false ? ' ✕' : ''}`),
          orphan: !!r.is_orphan,
          raw: r
        }));
    }

    return [];
  }, [snapshot, kind, search, relType, relKind, orgFilter, orphansOnly, gpsByGroupId, gpsByPeriodId]);

  const openCascade = async (gp) => {
    setCascadeTarget(gp);
    setCascadePreview(null);
    setCascadeLoading(true);
    setCascadeOpts({
      cascadeChildGps: (gp.hierarchy_children || 0) > 0,
      deleteOrphanGroup: false,
      deleteOrphanPeriod: false
    });
    try {
      const res = await fetch(`/api/data-cleanup-manager/groups-period/${gp.id}/cascade-preview`, authHeaders);
      const data = await res.json();
      if (!res.ok || data.success === false) throw new Error(data.error || 'Preview failed');
      setCascadePreview(data);
      setCascadeOpts({
        cascadeChildGps: (data.will_delete?.child_groups_periods || 0) > 0,
        deleteOrphanGroup: false,
        deleteOrphanPeriod: false
      });
    } catch (err) {
      setError(err.message || 'Failed to load cascade preview');
      setCascadeTarget(null);
    } finally {
      setCascadeLoading(false);
    }
  };

  const confirmCascadeDelete = async () => {
    if (!cascadeTarget) return;
    setBusyId(cascadeTarget.id);
    try {
      const res = await fetch(`/api/data-cleanup-manager/groups-period/${cascadeTarget.id}`, {
        method: 'DELETE',
        ...authHeaders,
        body: JSON.stringify(cascadeOpts)
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Delete failed');
      setCascadeTarget(null);
      setCascadePreview(null);
      await loadSnapshot();
    } catch (err) {
      alert(err.message || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteUgp = async (ugpId, userName) => {
    if (
      !window.confirm(
        `Delete enrollment for ${userName || 'this user'} and ALL attached student info, credentials, documents, person links, and form submissions?`
      )
    ) {
      return;
    }
    setBusyId(ugpId);
    try {
      const res = await fetch(`/api/data-cleanup-manager/ugp/${ugpId}`, {
        method: 'DELETE',
        ...authHeaders
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Delete failed');
      await loadSnapshot();
    } catch (err) {
      alert(err.message || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteSpine = async (table, id, label, cascadeGps) => {
    const path = table === 'groups' ? 'group' : 'period';
    setBusyId(id);
    try {
      const res = await fetch(`/api/data-cleanup-manager/${path}/${id}`, {
        method: 'DELETE',
        ...authHeaders,
        body: JSON.stringify({ cascadeGps })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Delete failed');
      setSpineTarget(null);
      await loadSnapshot();
    } catch (err) {
      alert(err.message || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const openSpineDelete = (row) => {
    const gps =
      row.table === 'groups'
        ? gpsByGroupId[String(row.id)] || []
        : gpsByPeriodId[String(row.id)] || [];
    const enrollments = gps.reduce((n, gp) => n + (gp.enrollments ?? 0), 0);
    if (
      row.table === 'periods' &&
      gps.length > 0 &&
      (row.permanent || isPermanentPeriod(row.raw))
    ) {
      alert('Permanent periods cannot be deleted while groups_periods still reference them');
      return;
    }
    if (gps.length === 0) {
      if (!window.confirm(`Remove orphan ${row.label || row.table} (${shortId(row.id)})?`)) return;
      handleDeleteSpine(row.table, row.id, row.label, false);
      return;
    }
    setSpineCascadeGps(true);
    setSpineTarget({
      table: row.table,
      id: row.id,
      label: row.label,
      gpCount: gps.length,
      enrollments
    });
  };

  const handleDeleteEntity = async (table, id, label) => {
    if (!window.confirm(`Remove ${label || table} (${shortId(id)})?`)) return;
    setBusyId(id);
    try {
      const res = await fetch(`/api/data-cleanup-manager/entity/${table}/${id}`, {
        method: 'DELETE',
        ...authHeaders
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Delete failed');
      await loadSnapshot();
    } catch (err) {
      alert(err.message || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const onRowDelete = (row) => {
    if (row.table === 'groups_periods') {
      openCascade(row.raw);
      return;
    }
    if (row.table === 'user_groups_periods') {
      handleDeleteUgp(row.id, row.label);
      return;
    }
    if (row.table === 'groups' || row.table === 'periods') {
      openSpineDelete(row);
      return;
    }
    if (ENTITY_DELETE_TABLES.has(row.table)) {
      handleDeleteEntity(row.table, row.id, row.label);
    }
  };

  const handleDeleteEntityOrSpine = (table, id, label) => {
    if (table === 'groups' || table === 'periods') {
      openSpineDelete({
        table,
        id,
        label,
        gpCount: 0,
        permanent: table === 'periods' ? isPermanentPeriod({ id, title: label }) : false,
        raw: { id, title: label }
      });
      return;
    }
    handleDeleteEntity(table, id, label);
  };

  const canDeleteRow = (row) =>
    row.table === 'groups_periods' ||
    row.table === 'user_groups_periods' ||
    row.table === 'groups' ||
    (row.table === 'periods' &&
      (row.gpCount === 0 || (!row.permanent && !isPermanentPeriod(row.raw)))) ||
    ENTITY_DELETE_TABLES.has(row.table);

  const orphanRelationships = useMemo(
    () => (snapshot?.relationships || []).filter((r) => r.is_orphan),
    [snapshot]
  );

  const orphanEntitiesForWeb = useMemo(() => {
    if (!snapshot) return [];
    const out = [...orphanRelationships];
    const push = (rows, table, labelFn) => {
      (rows || []).forEach((r) => {
        if (!r.is_orphan) return;
        out.push({
          id: r.id,
          relationship_type: labelFn(r),
          parent_table: table,
          parent_id: r.id,
          child_table: (r.missing_sides || [])[0] || 'missing',
          child_id: r.user_groups_periods_id || r.groups_periods_id || r.form_id || r.group_id || r.period_id || null,
          missing_sides: r.missing_sides || [],
          is_orphan: true,
          _deleteTable: table
        });
      });
    };
    push(snapshot.groups, 'groups', (r) => r.name || 'orphan group');
    push(snapshot.periods, 'periods', (r) => r.title || r.code || 'orphan period');
    push(snapshot.groups_periods, 'groups_periods', (r) => r.label || r.group_name || 'orphan GP');
    push(snapshot.user_groups_periods, 'user_groups_periods', (r) => r.user_name || r.email || 'orphan UGP');
    push(snapshot.student_info, 'student_info', (r) => r.status || 'student_info');
    push(snapshot.credentials_keys, 'credentials_keys', (r) => r.platform_label || r.title || 'credential');
    push(snapshot.documents, 'documents', (r) => r.title || r.file_name || 'document');
    push(snapshot.form_submissions, 'form_submissions', (r) => r.form_title || 'submission');
    return out;
  }, [snapshot, orphanRelationships]);

  const totalOrphans = counts.orphans?.total ?? orphanEntitiesForWeb.length;

  const mapOrgTree = useMemo(() => {
    if (!snapshot?.orgTrees) return null;
    if (orgFilter !== 'all' && snapshot.orgTrees[orgFilter]) {
      return snapshot.orgTrees[orgFilter];
    }
    // Web view needs one org tree — fall back to first organization.
    const first = organizations[0];
    if (first?.id && snapshot.orgTrees[String(first.id)]) {
      return snapshot.orgTrees[String(first.id)];
    }
    return null;
  }, [snapshot, orgFilter, organizations]);

  const webOrgId = mapOrgTree?.org?.id ? String(mapOrgTree.org.id) : null;

  const selectKind = (nextKind) => {
    setKind(nextKind);
    setOrphansOnly(false);
    setSearch('');
    if (nextKind !== 'relationships') {
      setRelKind('all');
      setRelType('all');
    }
    if (
      nextKind !== 'groups_periods' &&
      nextKind !== 'user_groups_periods' &&
      nextKind !== 'relationships'
    ) {
      setOrgFilter('all');
    }
  };

  const switchView = (mode) => {
    setViewMode(mode);
    if (mode === 'web' && orgFilter === 'all' && organizations[0]?.id) {
      setOrgFilter(String(organizations[0].id));
    }
  };

  const openWebGpDelete = (id, label) => {
    const gp = (snapshot?.groups_periods || []).find((g) => String(g.id) === String(id));
    if (gp) openCascade(gp);
    else openCascade({ id, label, hierarchy_children: 0 });
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Enrollment data</h1>
          <div style={styles.subtitle}>
            {viewMode === 'table'
              ? 'Flat table browser for the enrollment spine. Filter by table and relationship type; GP / group / period delete asks about cascade.'
              : 'Org → team GP → enrollment → attachments web. Hover × to delete; team GPs open the cascade dialog.'}
          </div>
        </div>
        <div style={styles.headerRight}>
          <div className="edm-view-tabs" role="tablist" aria-label="View mode">
            <div
              role="tab"
              tabIndex={0}
              aria-selected={viewMode === 'table'}
              className={['edm-view-tab', viewMode === 'table' && 'edm-view-tab--active']
                .filter(Boolean)
                .join(' ')}
              onMouseDown={(e) => e.button === 0 && e.preventDefault()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  switchView('table');
                }
              }}
              onClick={() => switchView('table')}
            >
              Table
            </div>
            <div
              role="tab"
              tabIndex={0}
              aria-selected={viewMode === 'web'}
              className={['edm-view-tab', viewMode === 'web' && 'edm-view-tab--active']
                .filter(Boolean)
                .join(' ')}
              onMouseDown={(e) => e.button === 0 && e.preventDefault()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  switchView('web');
                }
              }}
              onClick={() => switchView('web')}
            >
              Web
            </div>
          </div>
          <button type="button" style={styles.ghostButton} onClick={loadSnapshot} disabled={loading}>
            {loading ? 'Refreshing…' : 'Reload'}
          </button>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {loading && !snapshot ? (
        <div style={styles.statePad}>Loading tables…</div>
      ) : snapshot ? (
        viewMode === 'web' ? (
          <>
            <div style={styles.toolbar}>
              <div style={styles.toolbarLeft}>
                <select
                  value={webOrgId || orgFilter}
                  onChange={(e) => setOrgFilter(e.target.value)}
                  style={styles.selectWide}
                  aria-label="Organization"
                >
                  {organizations.length === 0 && <option value="">No organizations</option>}
                  {organizations.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label || o.group_name}
                    </option>
                  ))}
                </select>
                <label className="edm-show-ids">
                  <input
                    type="checkbox"
                    checked={webShowIds}
                    onChange={(e) => setWebShowIds(e.target.checked)}
                    onMouseDown={(e) => e.button === 0 && e.preventDefault()}
                  />
                  Show IDs
                </label>
                {mapOrgTree?.counts && (
                  <span style={styles.muted}>
                    {mapOrgTree.counts.teams ?? 0} teams · {mapOrgTree.counts.enrollments ?? 0} members ·{' '}
                    {mapOrgTree.counts.attachments ?? 0} attach
                    {orphanEntitiesForWeb.length > 0
                      ? ` · ${orphanEntitiesForWeb.length} orphan${orphanEntitiesForWeb.length === 1 ? '' : 's'}`
                      : ''}
                  </span>
                )}
              </div>
            </div>
            {mapOrgTree ? (
              <div style={styles.webPanel}>
                <OrgHierarchyWeb
                  orgTree={mapOrgTree}
                  orphans={orphanEntitiesForWeb}
                  showIds={webShowIds}
                  onDeleteTeamGp={openWebGpDelete}
                  onDeleteUgp={handleDeleteUgp}
                  onDeleteEntity={handleDeleteEntityOrSpine}
                  styles={styles}
                  fontStack={fontStack}
                  monoFont={monoFont}
                />
              </div>
            ) : orphanEntitiesForWeb.length > 0 ? (
              <div style={styles.webPanel}>
                <OrgHierarchyWeb
                  orgTree={{ org: { label: 'No organization', id: null }, teams: [], counts: {} }}
                  orphans={orphanEntitiesForWeb}
                  showIds={webShowIds}
                  onDeleteTeamGp={openWebGpDelete}
                  onDeleteUgp={handleDeleteUgp}
                  onDeleteEntity={handleDeleteEntityOrSpine}
                  styles={styles}
                  fontStack={fontStack}
                  monoFont={monoFont}
                />
              </div>
            ) : (
              <div style={styles.emptyState}>Pick an organization to view its hierarchy web.</div>
            )}
          </>
        ) : (
        <>
          <div style={styles.countBar}>
            {TABLE_KINDS.map((t) => {
              const n =
                t.id === 'relationships'
                  ? counts.relationships ?? counts.gp_relationships
                  : counts[t.id];
              const orphanN = counts.orphans?.[t.id] || 0;
              const selected = kind === t.id;
              return (
                <div
                  key={t.id}
                  role="button"
                  tabIndex={0}
                  className={['edm-count-chip', selected && 'edm-count-chip--active']
                    .filter(Boolean)
                    .join(' ')}
                  aria-pressed={selected}
                  aria-label={`${t.label}: ${n ?? 0}${orphanN ? `, ${orphanN} orphan` : ''}`}
                  onMouseDown={(e) => e.button === 0 && e.preventDefault()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      selectKind(t.id);
                    }
                  }}
                  onClick={() => selectKind(t.id)}
                >
                  <span className="edm-count-chip-short">{t.short}</span>
                  <span className="edm-count-chip-count">{n ?? 0}</span>
                  {orphanN > 0 && <span className="edm-count-chip-orphan">{orphanN}</span>}
                </div>
              );
            })}
            {totalOrphans > 0 && (
              <div
                role="button"
                tabIndex={0}
                className={['edm-count-chip', 'edm-count-chip--orphan-filter', orphansOnly && 'edm-count-chip--active']
                  .filter(Boolean)
                  .join(' ')}
                aria-pressed={orphansOnly}
                onMouseDown={(e) => e.button === 0 && e.preventDefault()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setOrphansOnly((v) => !v);
                  }
                }}
                onClick={() => setOrphansOnly((v) => !v)}
                title="Show only rows with missing FK targets"
              >
                <span className="edm-count-chip-short">Orphans</span>
                <span className="edm-count-chip-count">{totalOrphans}</span>
              </div>
            )}
          </div>

          <div style={styles.toolbar}>
            <div style={styles.toolbarLeft}>
              <select
                value={kind}
                onChange={(e) => selectKind(e.target.value)}
                style={styles.select}
                aria-label="Table"
              >
                {TABLE_KINDS.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter rows…"
                style={styles.searchInput}
              />
              {(kind === 'groups_periods' ||
                kind === 'user_groups_periods' ||
                kind === 'relationships') && (
                <select
                  value={orgFilter}
                  onChange={(e) => setOrgFilter(e.target.value)}
                  style={styles.select}
                  aria-label="Organization scope"
                >
                  <option value="all">All orgs</option>
                  {organizations.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label || o.group_name}
                    </option>
                  ))}
                </select>
              )}
              {kind === 'relationships' && (
                <>
                  <select
                    value={relKind}
                    onChange={(e) => setRelKind(e.target.value)}
                    style={styles.select}
                    aria-label="Relationship kind"
                  >
                    <option value="all">All kinds</option>
                    <option value="orphan">
                      orphan{counts.orphan_relationships != null ? ` (${counts.orphan_relationships})` : ''}
                    </option>
                    <option value="hierarchy">hierarchy</option>
                    <option value="gp_link">gp_link</option>
                    <option value="person_link">person_link</option>
                    <option value="other">other</option>
                  </select>
                  <select
                    value={relType}
                    onChange={(e) => setRelType(e.target.value)}
                    style={styles.select}
                    aria-label="Relationship type"
                  >
                    {relTypeOptions.map((t) => (
                      <option key={t} value={t}>
                        {t === 'all' ? 'All types' : t}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
            <div style={styles.toolbarRight}>
              <span style={styles.muted}>{rows.length} row{rows.length === 1 ? '' : 's'}</span>
            </div>
          </div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, ...styles.colIdx }}>#</th>
                  <th style={styles.th}>id</th>
                  <th style={styles.th}>label</th>
                  <th style={styles.th}>meta</th>
                  <th style={styles.th}>detail</th>
                  <th style={{ ...styles.th, ...styles.colAct }} />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={styles.emptyState}>
                      No rows for this filter.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, i) => (
                    <tr
                      key={row.key}
                      style={row.orphan ? styles.tableRowOrphan : undefined}
                    >
                      <td style={{ ...styles.td, ...styles.colIdx }}>{i + 1}</td>
                      <td style={{ ...styles.td, ...styles.colId }}>
                        <span style={styles.mono}>{String(row.id)}</span>
                      </td>
                      <td style={{ ...styles.td, ...styles.colLabel }}>
                        {row.orphan && <span style={styles.orphanTag}>orphan</span>}
                        {row.label}
                      </td>
                      <td style={{ ...styles.td, ...styles.colMeta }}>{row.meta}</td>
                      <td style={{ ...styles.td, ...styles.colExtra }}>{row.extra}</td>
                      <td style={{ ...styles.td, ...styles.colAct }}>
                        {canDeleteRow(row) && (
                          <button
                            type="button"
                            style={styles.dangerButton}
                            disabled={busyId === row.id}
                            onClick={() => onRowDelete(row)}
                          >
                            {row.table === 'groups_periods' ||
                            ((row.table === 'groups' || row.table === 'periods') && row.gpCount > 0)
                              ? 'Cascade…'
                              : 'Del'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
        )
      ) : (
        <div style={styles.emptyState}>
          Could not load data.{' '}
          <button type="button" style={styles.ghostButton} onClick={loadSnapshot}>
            Retry
          </button>
        </div>
      )}

      {cascadeTarget && (
        <CascadeDialog
          loading={cascadeLoading}
          preview={cascadePreview}
          target={cascadeTarget}
          opts={cascadeOpts}
          setOpts={setCascadeOpts}
          busy={busyId === cascadeTarget.id}
          onCancel={() => {
            setCascadeTarget(null);
            setCascadePreview(null);
          }}
          onConfirm={confirmCascadeDelete}
        />
      )}

      {spineTarget && (
        <SpineDeleteDialog
          target={spineTarget}
          cascadeGps={spineCascadeGps}
          setCascadeGps={setSpineCascadeGps}
          busy={busyId === spineTarget.id}
          onCancel={() => setSpineTarget(null)}
          onConfirm={() =>
            handleDeleteSpine(spineTarget.table, spineTarget.id, spineTarget.label, spineCascadeGps)
          }
        />
      )}
    </div>
  );
};

function SpineDeleteDialog({ target, cascadeGps, setCascadeGps, busy, onCancel, onConfirm }) {
  const isGroup = target.table === 'groups';
  const noun = isGroup ? 'group' : 'period';
  const gpCount = target.gpCount || 0;
  const enrollments = target.enrollments || 0;
  const canConfirm = gpCount === 0 || cascadeGps;

  return (
    <div style={styles.modalBackdrop} role="dialog" aria-modal="true">
      <div style={styles.modal}>
        <div style={styles.modalTitle}>Delete {noun}</div>
        <div style={styles.modalSub}>
          {target.label || target.id}
          <span style={styles.monoMuted}> · {shortId(target.id)}</span>
        </div>

        <div style={styles.cascadeBlock}>
          <div style={styles.cascadeLabel}>Always removed with this {noun}</div>
          <ul style={styles.cascadeList}>
            <li>This {isGroup ? 'groups' : 'periods'} row</li>
            <li>Relationship rows pointing at this {noun}</li>
          </ul>
        </div>

        <label style={styles.checkRow}>
          <input
            type="checkbox"
            checked={cascadeGps}
            onChange={(e) => setCascadeGps(e.target.checked)}
            disabled={!gpCount}
          />
          <span>
            Also delete <strong>{gpCount}</strong> groups_periods that use this {noun}
            {enrollments
              ? ` (${enrollments} enrollment${enrollments === 1 ? '' : 's'} and their app data)`
              : ''}
            <span style={styles.hint}>
              {gpCount
                ? ' — required while GPs still reference this row; uncheck blocks delete'
                : ' — no GPs reference this row'}
            </span>
          </span>
        </label>

        <div style={styles.modalActions}>
          <button type="button" style={styles.ghostButton} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            style={styles.dangerSolid}
            onClick={onConfirm}
            disabled={busy || !canConfirm}
          >
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CascadeDialog({ loading, preview, target, opts, setOpts, busy, onCancel, onConfirm }) {
  const will = preview?.will_delete;
  const group = preview?.group;
  const period = preview?.period;

  return (
    <div style={styles.modalBackdrop} role="dialog" aria-modal="true">
      <div style={styles.modal}>
        <div style={styles.modalTitle}>Delete groups_periods</div>
        <div style={styles.modalSub}>
          {target.label || preview?.gp?.label || target.id}
          <span style={styles.monoMuted}> · {shortId(target.id)}</span>
        </div>

        {loading || !preview ? (
          <div style={styles.statePad}>Computing cascade…</div>
        ) : (
          <>
            <div style={styles.cascadeBlock}>
              <div style={styles.cascadeLabel}>Always removed with this GP</div>
              <ul style={styles.cascadeList}>
                <li>This groups_periods row</li>
                <li>
                  {will?.enrollments ?? 0} enrollment{will?.enrollments === 1 ? '' : 's'} (UGP) and their app
                  data (student_info, credentials, documents, form_submissions, person links)
                </li>
                <li>{will?.relationships ?? 0} polymorphic relationship row(s) touching this GP</li>
              </ul>
            </div>

            <label style={styles.checkRow}>
              <input
                type="checkbox"
                checked={opts.cascadeChildGps}
                onChange={(e) => setOpts((o) => ({ ...o, cascadeChildGps: e.target.checked }))}
                disabled={!will?.child_groups_periods}
              />
              <span>
                Also delete <strong>{will?.child_groups_periods ?? 0}</strong> child team GP
                {will?.child_groups_periods === 1 ? '' : 's'}
                {will?.child_enrollments
                  ? ` (${will.child_enrollments} enrollment${will.child_enrollments === 1 ? '' : 's'})`
                  : ''}
                <span style={styles.hint}>
                  {' '}
                  — hierarchy under this GP via team_nested. Off = only unlink relationships; child GPs remain.
                </span>
              </span>
            </label>

            <label style={styles.checkRow}>
              <input
                type="checkbox"
                checked={opts.deleteOrphanGroup}
                onChange={(e) => setOpts((o) => ({ ...o, deleteOrphanGroup: e.target.checked }))}
                disabled={!group?.can_orphan_delete}
              />
              <span>
                Also delete group <strong>{group?.name || '—'}</strong>
                <span style={styles.hint}>
                  {group?.can_orphan_delete
                    ? ' — no other GPs reference this group'
                    : ` — still used by ${group?.other_gps ?? 0} other GP(s); cannot orphan-delete`}
                </span>
              </span>
            </label>

            <label style={styles.checkRow}>
              <input
                type="checkbox"
                checked={opts.deleteOrphanPeriod}
                onChange={(e) => setOpts((o) => ({ ...o, deleteOrphanPeriod: e.target.checked }))}
                disabled={!period?.can_orphan_delete}
              />
              <span>
                Also delete period <strong>{period?.title || period?.code || '—'}</strong>
                <span style={styles.hint}>
                  {!period
                    ? ' — no period on this GP'
                    : period.is_permanent
                      ? ' — permanent periods cannot be deleted'
                      : period.can_orphan_delete
                        ? ' — no other GPs reference this period'
                        : ` — still used by ${period.other_gps ?? 0} other GP(s)`}
                </span>
              </span>
            </label>
          </>
        )}

        <div style={styles.modalActions}>
          <button type="button" style={styles.ghostButton} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            style={styles.dangerSolid}
            onClick={onConfirm}
            disabled={busy || loading || !preview}
          >
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

const shortId = (id) => {
  const s = String(id || '');
  if (s.length <= 12) return s;
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
};

const colors = {
  text: '#0f172a',
  textSecondary: '#475569',
  textMuted: '#94a3b8',
  border: '#e2e8f0',
  surface: '#ffffff',
  surfaceAlt: '#f8fafc',
  surfaceHover: '#f1f5f9',
  danger: '#b91c1c',
  dangerBorder: '#fecaca',
  errorText: '#991b1b',
  errorBg: '#fef2f2',
  errorBorder: '#fecaca'
};

const fontStack = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const monoFont = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const styles = {
  container: {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    margin: '8px 0',
    padding: '10px 12px',
    backgroundColor: colors.surface,
    borderRadius: '0',
    border: `1px solid ${colors.border}`,
    borderLeft: 'none',
    borderRight: 'none',
    fontFamily: fontStack,
    color: colors.text,
    fontSize: '0.8rem'
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '10px',
    marginBottom: '10px'
  },
  title: { fontSize: '0.95rem', fontWeight: 600, margin: 0 },
  subtitle: {
    fontSize: '0.72rem',
    color: colors.textSecondary,
    marginTop: '3px',
    maxWidth: '720px',
    lineHeight: 1.4
  },
  error: {
    padding: '6px 9px',
    backgroundColor: colors.errorBg,
    color: colors.errorText,
    borderRadius: '5px',
    marginBottom: '8px',
    border: `1px solid ${colors.errorBorder}`,
    fontSize: '0.75rem'
  },
  countBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginBottom: '8px'
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    flexWrap: 'wrap',
    marginBottom: '8px'
  },
  toolbarLeft: { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
  toolbarRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  select: {
    padding: '4px 6px',
    fontSize: '0.72rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontFamily: fontStack,
    background: colors.surface,
    maxWidth: '180px'
  },
  searchInput: {
    minWidth: '160px',
    padding: '4px 8px',
    fontSize: '0.72rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontFamily: fontStack
  },
  toggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '0.7rem',
    color: colors.textSecondary,
    cursor: 'pointer'
  },
  muted: { color: colors.textMuted, fontSize: '0.7rem' },
  mono: { fontFamily: monoFont, fontSize: '0.68rem' },
  monoMuted: { fontFamily: monoFont, fontSize: '0.68rem', color: colors.textMuted },
  tableWrap: {
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    width: '100%',
    maxHeight: 'calc(100vh - 220px)',
    overflowX: 'auto',
    overflowY: 'auto'
  },
  table: {
    width: 'max-content',
    minWidth: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'auto'
  },
  th: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    background: colors.surfaceAlt,
    borderBottom: `1px solid ${colors.border}`,
    padding: '5px 10px',
    fontSize: '0.65rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: colors.textMuted,
    textAlign: 'left',
    whiteSpace: 'nowrap'
  },
  td: {
    padding: '4px 10px',
    borderTop: `1px solid ${colors.border}`,
    verticalAlign: 'middle',
    whiteSpace: 'nowrap'
  },
  tableRowOrphan: {
    background: '#fffbeb'
  },
  orphanTag: {
    display: 'inline-block',
    marginRight: '5px',
    padding: '0 4px',
    fontSize: '0.58rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#92400e',
    background: '#fde68a',
    borderRadius: '3px',
    verticalAlign: 'middle'
  },
  colIdx: {
    fontFamily: monoFont,
    fontSize: '0.62rem',
    color: colors.textMuted,
    textAlign: 'right',
    width: '1%',
    whiteSpace: 'nowrap'
  },
  colId: { whiteSpace: 'nowrap' },
  colLabel: { fontWeight: 500, whiteSpace: 'nowrap' },
  colMeta: { color: colors.textSecondary, whiteSpace: 'nowrap' },
  colExtra: { color: colors.textMuted, whiteSpace: 'nowrap', fontSize: '0.68rem' },
  colAct: { textAlign: 'right', whiteSpace: 'nowrap', width: '1%' },
  statePad: { padding: '20px', textAlign: 'center', color: colors.textSecondary },
  emptyState: {
    padding: '12px',
    color: colors.textMuted,
    textAlign: 'center',
    background: colors.surfaceAlt,
    fontSize: '0.75rem'
  },
  ghostButton: {
    padding: '3px 8px',
    background: 'transparent',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '0.72rem',
    cursor: 'pointer',
    fontFamily: fontStack
  },
  dangerButton: {
    padding: '2px 6px',
    color: colors.danger,
    border: `1px solid ${colors.dangerBorder}`,
    borderRadius: '4px',
    fontSize: '0.65rem',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: fontStack
  },
  dangerSolid: {
    padding: '5px 12px',
    color: '#fff',
    background: colors.danger,
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.75rem',
    cursor: 'pointer',
    fontFamily: fontStack
  },
  headerRight: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
  selectWide: {
    padding: '4px 6px',
    fontSize: '0.72rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontFamily: fontStack,
    background: colors.surface,
    minWidth: '220px',
    maxWidth: '360px'
  },
  webPanel: {
    width: '100%',
    minHeight: 'calc(100vh - 200px)'
  },
  mapWrap: { marginBottom: '8px' },
  webWrap: {
    position: 'relative',
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    background: '#f4f7fb',
    overflow: 'hidden',
    minHeight: 'calc(100vh - 220px)'
  },
  webCanvasToolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 10px',
    borderBottom: `1px solid ${colors.border}`,
    background: 'rgba(255,255,255,0.82)'
  },
  webCanvasToolCluster: {
    display: 'inline-flex',
    border: `1px solid ${colors.border}`,
    borderRadius: '5px',
    overflow: 'hidden',
    background: '#fff'
  },
  webCanvasToolBtn: {
    width: '24px',
    height: '22px',
    border: 'none',
    borderRight: `1px solid ${colors.border}`,
    cursor: 'pointer'
  },
  webCanvasToolBtnEnd: { borderRight: 'none' },
  webCanvasToolBtnFit: {
    height: '22px',
    padding: '0 7px',
    borderRadius: '5px',
    border: `1px solid ${colors.border}`,
    background: '#fff',
    fontSize: '10px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  webCanvasHint: { marginLeft: 'auto', fontSize: '10px', color: colors.textMuted },
  webCanvasViewport: {
    position: 'relative',
    overflow: 'hidden',
    minHeight: '420px',
    height: 'calc(100vh - 260px)',
    background: '#f1f5f9',
    cursor: 'grab',
    touchAction: 'none',
    userSelect: 'none'
  },
  webSvg: { display: 'block', overflow: 'visible' },
  emptyWebHint: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: '8px',
    textAlign: 'center',
    fontSize: '0.72rem',
    color: colors.textMuted
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '16px'
  },
  modal: {
    width: 'min(480px, 100%)',
    background: colors.surface,
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
    padding: '14px 16px',
    boxShadow: '0 12px 40px rgba(15, 23, 42, 0.18)'
  },
  modalTitle: { fontSize: '0.85rem', fontWeight: 600 },
  modalSub: { fontSize: '0.75rem', color: colors.textSecondary, marginTop: '2px', marginBottom: '10px' },
  cascadeBlock: {
    padding: '8px 10px',
    background: colors.surfaceAlt,
    border: `1px solid ${colors.border}`,
    borderRadius: '5px',
    marginBottom: '8px'
  },
  cascadeLabel: {
    fontSize: '0.65rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: colors.textMuted,
    fontWeight: 600,
    marginBottom: '4px'
  },
  cascadeList: {
    margin: 0,
    paddingLeft: '16px',
    fontSize: '0.72rem',
    color: colors.textSecondary,
    lineHeight: 1.45
  },
  checkRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    fontSize: '0.72rem',
    marginBottom: '8px',
    cursor: 'pointer',
    lineHeight: 1.4
  },
  hint: { color: colors.textMuted, fontWeight: 400 },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '12px',
    paddingTop: '10px',
    borderTop: `1px solid ${colors.border}`
  }
};

export default EnrollmentDataManager;
